import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";

function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    div.setAttribute("data-portal-modal", "true");
    div.setAttribute("translate", "no");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

const formatearFechaLocal = (fechaString) => {
  if (!fechaString) return "N/A";
  const partes = fechaString.split('T')[0].split('-');
  if (partes.length === 3) return `${partes[2]}/${partes[1]}/${partes[0]}`;
  return fechaString;
};

export default function HistorialPermisos({ usuario, filtroABAC, onBack }) {
  const [tabActivo, setTabActivo] = useState('FO_TH_96');
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [detalleSeleccionado, setDetalleSeleccionado] = useState(null);

  const cargarHistorial = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;
      
      const res = await fetch(`${API_URL}/api/th/historial/${encodeURIComponent(tabActivo)}`, {
        method: "GET",
        headers: { 
          "Content-Type": "application/json", 
          ...(token && { Authorization: `Bearer ${token}` }),
          "x-user-data": JSON.stringify(usuario),
          "x-abac-permissions": JSON.stringify({ "Historial de Solicitudes": filtroABAC })
        }
      });
      
      if (!res.ok) throw new Error(`Error del servidor: ${res.status}`);
      const data = await res.json();
      if (data && data.success) {
        setRegistros(data.data || []);
      } else {
        setRegistros([]);
      }
    } catch (error) {
      setRegistros([]);
    } finally {
      setLoading(false);
    }
  }, [tabActivo, usuario, filtroABAC]);

  useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

  const abrirDetalle = (registro) => {
    setDetalleSeleccionado(registro); 
  };

  const registrosFiltrados = registros.filter(r => {
    const textoBusqueda = busqueda.toLowerCase();
    const nombre = (r.nombres_empleado || r.nombre_empleado || r.solicitante_nombre || "").toLowerCase();
    const sede = (r.sede || r.centro_trabajo || r.sede_solicitante || r.sede_destino || "").toLowerCase();
    const doc = (r.documento || r.documento_empleado || "").toLowerCase();
    return nombre.includes(textoBusqueda) || sede.includes(textoBusqueda) || doc.includes(textoBusqueda);
  });

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-6xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      <div className="bg-transparent border-b border-white/20 px-4 py-3 sm:px-8 sm:py-5 z-40 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button onClick={onBack} className="w-10 h-10 flex-none bg-white/60 hover:bg-white/80 text-slate-700 hover:text-emerald-600 flex items-center justify-center rounded-full transition-all shadow-sm border border-white/40 cursor-pointer backdrop-blur-sm">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path></svg>
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-slate-800 leading-tight">Historial de Solicitudes</h1>
            <p className="text-[11px] font-bold text-slate-600 mt-0.5">Trazabilidad de gestión humana</p>
          </div>
        </div>
        <div className="w-full sm:w-80 relative">
          <input type="text" placeholder="Buscar por colaborador, sede o documento..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="w-full bg-white/60 backdrop-blur-md border border-white/60 rounded-xl px-4 py-2 text-xs font-semibold text-slate-800 outline-none focus:border-emerald-400 focus:bg-white shadow-sm transition-all pl-9" />
          <span className="absolute left-3 top-2.5 text-slate-400">🔍</span>
        </div>
      </div>

      <div className="flex px-4 sm:px-8 pt-4 gap-2 border-b border-white/20 shrink-0 overflow-x-auto no-scrollbar">
        <button onClick={() => setTabActivo('FO_TH_96')} className={`px-4 py-2 text-[10px] sm:text-xs font-black uppercase rounded-t-xl transition-all whitespace-nowrap ${tabActivo === 'FO_TH_96' ? 'bg-emerald-600 text-white shadow-md' : 'bg-white/40 text-slate-600'}`}>📝 Permisos</button>
        <button onClick={() => setTabActivo('FO_TH_97')} className={`px-4 py-2 text-[10px] sm:text-xs font-black uppercase rounded-t-xl transition-all whitespace-nowrap ${tabActivo === 'FO_TH_97' ? 'bg-blue-600 text-white shadow-md' : 'bg-white/40 text-slate-600'}`}>🔄 Traslados</button>
        <button onClick={() => setTabActivo('FO_TH_98')} className={`px-4 py-2 text-[10px] sm:text-xs font-black uppercase rounded-t-xl transition-all whitespace-nowrap ${tabActivo === 'FO_TH_98' ? 'bg-amber-600 text-white shadow-md' : 'bg-white/40 text-slate-600'}`}>💰 Cesantías</button>
        <button onClick={() => setTabActivo('FOR_TH_99')} className={`px-4 py-2 text-[10px] sm:text-xs font-black uppercase rounded-t-xl transition-all whitespace-nowrap ${tabActivo === 'FOR_TH_99' ? 'bg-indigo-600 text-white shadow-md' : 'bg-white/40 text-slate-600'}`}>🏖️ Vacaciones</button>
        <button onClick={() => setTabActivo('FO_TH_100')} className={`px-4 py-2 text-[10px] sm:text-xs font-black uppercase rounded-t-xl transition-all whitespace-nowrap ${tabActivo === 'FO_TH_100' ? 'bg-purple-600 text-white shadow-md' : 'bg-white/40 text-slate-600'}`}>👤 Requisición</button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-8 space-y-3 custom-scrollbar">
        {loading ? (
          <div className="text-center py-20"><span className="text-4xl animate-bounce inline-block mb-2">⏳</span><p className="text-sm font-bold text-slate-600">Cargando...</p></div>
        ) : registrosFiltrados.length === 0 ? (
          <div className="text-center py-20 bg-white/30 backdrop-blur-md rounded-2xl border border-dashed border-white/50 shadow-inner"><p className="text-sm font-bold text-slate-600">No hay registros.</p></div>
        ) : (
          registrosFiltrados.map((reg) => {
            const nombreColab = reg.nombres_empleado || reg.nombre_empleado || reg.solicitante_nombre || "Colaborador";
            const sedeColab = reg.sede || reg.centro_trabajo || reg.sede_solicitante || reg.sede_destino || "OFICINAS";
            const fechaRadicacion = reg.created_at || reg.fecha_solicitud || reg.fecha;

            return (
              <div key={reg.id} className="bg-white/50 hover:bg-white/70 p-4 rounded-2xl border border-white/60 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-black shrink-0 shadow-inner border bg-emerald-100/80 text-emerald-700">📄</div>
                  <div>
                    <span className="bg-emerald-600 text-white text-[9px] font-black px-2 py-0.5 rounded uppercase shadow-sm mb-1 inline-block">{tabActivo}</span>
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">{nombreColab}</h3>
                    <p className="text-[11px] font-bold text-slate-600 mt-0.5">Sede: <span className="text-slate-800">{sedeColab}</span></p>
                  </div>
                </div>
                <div className="flex items-center justify-between md:justify-end gap-6 w-full md:w-auto">
                  <div className="text-left md:text-right">
                    <p className="text-[9px] uppercase font-black text-slate-500">Radicación</p>
                    <p className="text-xs font-bold text-slate-700">{formatearFechaLocal(fechaRadicacion)}</p>
                  </div>
                  <button onClick={() => abrirDetalle(reg)} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-xl text-xs font-black shadow-md cursor-pointer border border-emerald-400">VER DETALLE</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {detalleSeleccionado && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/90 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-3xl w-full shadow-2xl border border-white flex flex-col relative overflow-hidden" style={{ maxHeight: '90vh' }}>
              <div className="flex justify-between items-center border-b border-slate-200 pb-4 mb-4 flex-shrink-0">
                <div>
                  <span className="bg-emerald-100 text-emerald-800 text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase">Detalle del Registro</span>
                  <h2 className="text-lg font-black text-slate-900 uppercase mt-1">
                    {detalleSeleccionado.nombres_empleado || detalleSeleccionado.nombre_empleado || detalleSeleccionado.solicitante_nombre || "Colaborador"}
                  </h2>
                  <p className="text-xs text-slate-500 font-medium">
                    Documento: {detalleSeleccionado.documento || detalleSeleccionado.documento_empleado || "N/A"} | Sede: {detalleSeleccionado.sede || detalleSeleccionado.centro_trabajo || detalleSeleccionado.sede_solicitante || "N/A"}
                  </p>
                </div>
                <button onClick={() => setDetalleSeleccionado(null)} className="w-8 h-8 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full font-black cursor-pointer shadow-inner">✕</button>
              </div>
              <div className="overflow-y-auto flex-1 pr-2 space-y-4 pb-6 custom-scrollbar">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2">Datos Radicados</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(detalleSeleccionado).map(([key, val]) => {
                    if (['id', 'firma_base64', 'documento_pdf', 'token_biometrico'].includes(key)) return null;
                    if (val === null || val === "" || val === undefined) return null;
                    return (
                      <div key={key} className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                        <p className="text-[10px] font-black text-slate-400 uppercase">{key.replace(/_/g, " ")}</p>
                        <p className="text-xs font-bold text-slate-800 mt-0.5">{String(val)}</p>
                      </div>
                    );
                  })}
                </div>

                {(detalleSeleccionado.firma_base64 || detalleSeleccionado.documento_pdf) && (
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <h4 className="text-xs font-black text-slate-400 uppercase mb-3">Evidencias y Soportes</h4>
                    <div className="flex flex-wrap gap-4">
                      {detalleSeleccionado.firma_base64 && (
                        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col justify-center items-center shadow-inner w-full sm:max-w-[200px]">
                          <p className="text-[10px] font-black text-slate-500 uppercase mb-2">Firma Digital</p>
                          <img src={detalleSeleccionado.firma_base64} alt="Firma" className="max-h-20 w-auto object-contain mix-blend-multiply" />
                        </div>
                      )}
                      {detalleSeleccionado.documento_pdf && (
                        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col justify-center items-center shadow-inner w-full sm:max-w-[200px]">
                          <p className="text-[10px] font-black text-slate-500 uppercase mb-2">Documento PDF</p>
                          <a href={detalleSeleccionado.documento_pdf} download="Soporte_Cesantias.pdf" className="text-xs bg-blue-100 text-blue-700 px-3 py-1.5 font-bold rounded-lg hover:bg-blue-200 transition-colors">Descargar PDF</a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}